<template>
	<form
		:class="props.formConfig.textCssClass"
		name="leadCapture"
		class="lead-capture position-relative"
		@submit.prevent
	>
		<div
			:class="{ 'display-flex-lg align-items-flex-start flex-wrap-wrap': !props.formConfig.fullWidth }"
			class="lead-capture__fieldset"
		>
			<InputField
				v-if="requireEmail"
				:class="[
					{ 'flex-1-lg mr-50-lg': !props.formConfig.fullWidth, 'mb-200': props.formConfig.fullWidth },
					'overflow-hidden-lg'
				]"
				:field="v$.email"
				field-name="Email"
				aria-labelledby="email"
			>
				<template #label>
					<span class="show-for-sr">Email address</span>
				</template>
				<template #input>
					<input
						id="email"
						v-model="v$.email.$model"
						class="input"
						:placeholder="placeholder"
						name="email"
						type="email"
						aria-label="Email address"
						:data-test="componentId ? `${componentId}-email-input` : 'email-input'"
					/>
				</template>
			</InputField>
			<div
				:class="{
					'pt-50 pt-0-lg': !props.formConfig.fullWidth,
					'width-100': props.formConfig.fullWidth,
					'mr-auto ml-auto': props.formConfig.centerButton
				}"
				class="email-submit__button--container text-align-right-lg mt-25-lg"
			>
				<SubmitButton
					:classes="buttonClasses"
					:data-test="componentId ? `${componentId}-lead-capture-submit` : 'lead-capture-submit'"
					:is-working="buttonClicked"
					:v$="v$"
					@sb-click="submitEmail()"
				>
					{{ props.formConfig.buttonText ? props.formConfig.buttonText : 'Get started' }}
				</SubmitButton>
			</div>
		</div>
		<ThirdPartyAuth
			v-if="thirdPartyAuthStore.isSignupEnabled"
			:display-disclaimer="true"
			:disclaimer-class="props.formConfig.thirdPartyAuthDisclaimerClass"
			:is-working="thirdPartyAuthSubmitted"
			:theme="props.formConfig.thirdPartyAuthTheme"
			@apple-auth-payload="(payload) => handleThirdPartyAuthPayload('apple', payload)"
			@google-auth-payload="(payload) => handleThirdPartyAuthPayload('google', payload)"
		/>
	</form>
</template>

<script setup lang="ts">
import { AppleAuthParameters, GoogleAuthParameters, ThirdPartyAuthType } from 'types/third-party-auth';
import { computed, reactive, ref, Ref } from 'vue';
import { email, required } from '@vuelidate/validators';
import { EmailCaptureFields, LeadCaptureFormConfig, LeadParameters } from 'types/layout/lead';
import { getItemFromLocalStorage, getItemFromSessionStorage, setItemInSessionStorage } from '@utils/web-storage';
import { isMobileOS, redirect } from '@utils/client';
import { useRoute, useRouter } from 'vue-router';
import { captureLead } from '@api/lead-capture';
import { constructUrlWithQueryParams } from '@utils/request';
import InputField from '@components/form/input-field.vue';
import { Link } from 'types/layout';
import MobileRedirectModal from '@components/modals/mobile-redirect-modal.vue';
import SubmitButton from '@components/form/submit-button.vue';
import ThirdPartyAuth from '@components/third-party-auth/third-party-auth.vue';
import { useAppStore } from '@stores/app';
import { useDebounceFn } from '@vueuse/core';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';
import { useVuelidate } from '@vuelidate/core';

interface Props {
	formConfig: LeadCaptureFormConfig;
	componentId?: string;
}

interface LeadCaptureFormState {
	email: string;
}

const props = withDefaults(defineProps<Props>(), { componentId: '' });

const state: LeadCaptureFormState = reactive({
	email: getItemFromLocalStorage('emailcapture') ?? ''
});

const rules = {
	email: { required, email }
};

const v$ = useVuelidate(rules, state);

const route = useRoute();
const router = useRouter();

const appStore = useAppStore();
const thirdPartyAuthStore = useThirdPartyAuthStore();

const leadCaptured = ref(false);

const leadParams: Ref<LeadParameters> = ref({
	email: state.email,
	referrer: '',
	signupFlowType: props.formConfig.signupFlowType
});

const buttonClicked = ref(false);
const thirdPartyAuthSubmitted = ref(false);

const placeholder = computed((): string => props.formConfig.placeholder || 'Email address');

const buttonClasses = computed((): string => {
	let classes = '';

	if (appStore.screenSize !== 'DESKTOP') {
		classes += 'width-100 ';
	}

	classes += props.formConfig.buttonClass
		? props.formConfig.buttonClass
		: 'button-primary lead-capture__button-primary';

	if (thirdPartyAuthSubmitted.value) {
		classes += ' disabled';
	}

	return classes;
});

const requireEmail = computed((): boolean => {
	const hasFeeWaiver = !!props.formConfig.feeWaiver;
	const showInput = props.formConfig.forceShowInput ?? false;
	return !leadCaptured.value || hasFeeWaiver || showInput;
});

const isMobileDevice = computed((): boolean => isMobileOS());

const submitEmail = useDebounceFn(async (): Promise<void> => {
	v$.value.$touch();

	if (!v$.value.$invalid) {
		buttonClicked.value = true;

		try {
			await submitLeadCapture();
		} catch {
			buttonClicked.value = false;
		}
	}
}, 200);

function openMobileRedirectModal(continueLink: Omit<Link, 'text'>): void {
	appStore.updateCurrentModal({
		modal: MobileRedirectModal,
		props: {
			headerText:
				leadParams.value.promoInvestmentType === 'FIRST_TIME_INVESTOR' ? 'Your promo is applied' : undefined,
			bodyText:
				leadParams.value.promoInvestmentType === 'FIRST_TIME_INVESTOR'
					? 'Continue with a general investing account on web or mobile app to claim your $10 in shares.'
					: undefined,
			continueLink: {
				text: 'No thanks, continue on mobile browser',
				router: continueLink.router,
				query: continueLink.query
			}
		}
	});
}

async function handleThirdPartyAuthPayload(
	type: ThirdPartyAuthType,
	authParameters: GoogleAuthParameters | AppleAuthParameters
): Promise<void> {
	try {
		thirdPartyAuthSubmitted.value = true;
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { email: _email, ...leadParameters } = leadParams.value;
		const payload = { ...authParameters, ...leadParameters };
		const routeName = await thirdPartyAuthStore.signUp(type, payload);

		if (isMobileDevice.value) {
			openMobileRedirectModal({ router: routeName });
		} else {
			await router.push({ name: routeName });
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		const error = e.response.data;
		if (error.error === 'invalid_grant' && error.reason === 'locked_user') {
			await router.push({ name: 'login-locked-out' });
		} else if (error.fieldErrorMap?.email) {
			await router.push({ name: 'login' });
		}
	} finally {
		thirdPartyAuthSubmitted.value = false;
	}
}

async function submitLeadCapture(): Promise<void> {
	try {
		leadParams.value.email = state.email;
		const leadResponseData: EmailCaptureFields = await captureLead(leadParams.value);
		if (leadResponseData.existingUser) {
			await router.push({ name: 'login' });
		} else if (props.formConfig.redirectCallback) {
			props.formConfig.redirectCallback(leadResponseData);
		} else {
			redirectAfterLeadCapture(leadResponseData);
		}
	} catch {}
}

async function redirectAfterLeadCapture(leadResponseData: EmailCaptureFields): Promise<void> {
	const destination = '/get-started';
	const redirectUrl = constructUrlWithQueryParams(destination, getAnalyticsParams(leadResponseData));

	if (isMobileDevice.value) {
		openMobileRedirectModal({ router: 'checkout-password', query: getAnalyticsParams(leadResponseData) });
	} else {
		redirect(redirectUrl);
	}
}

function getAnalyticsParams(leadResponseData: EmailCaptureFields): Record<string, string> {
	const params: Record<string, string> = {};

	if (leadResponseData.newLead) {
		params.new_lead = String(leadResponseData.newLead);
	}
	if (leadResponseData.mixpanelId) {
		params.distinct_id = String(leadResponseData.mixpanelId);
	}
	if (props.formConfig.feeWaiver) {
		params.feeWaiver = 'true';
	}

	return params;
}

function configureLeadParameters(): void {
	let sessionStorageLeadParams: LeadParameters | undefined = undefined;

	const loadCaptureParams = getItemFromSessionStorage('lead-capture-params');

	if (loadCaptureParams) {
		sessionStorageLeadParams = JSON.parse(loadCaptureParams) as LeadParameters;
	}

	const pennyHoarderId = route?.query.tph ? route?.query.tph.toString() : sessionStorageLeadParams?.pennyHoarderId;

	const impactRadiusId = route?.query.clickid
		? route?.query.clickid.toString()
		: sessionStorageLeadParams?.impactRadiusId;

	const utmCampaign = route?.query.utm_campaign
		? route?.query.utm_campaign.toString()
		: sessionStorageLeadParams?.utmCampaign;

	const utmContent = route?.query.utm_content
		? route?.query.utm_content.toString()
		: sessionStorageLeadParams?.utmContent;

	const utmMedium = route?.query.utm_medium
		? route?.query.utm_medium.toString()
		: sessionStorageLeadParams?.utmMedium;

	const utmSource = route?.query.utm_source
		? route?.query.utm_source.toString()
		: sessionStorageLeadParams?.utmSource;

	leadParams.value = {
		email: state.email,
		referrer: window.location.href,
		promoInvestmentType: props.formConfig.promoInvestmentType,
		riaFeeWaiverOriginType: props.formConfig.feeWaiver || sessionStorageLeadParams?.riaFeeWaiverOriginType,
		referralKey: props.formConfig.referralKey,
		signupFlowType: leadParams.value.signupFlowType,
		pennyHoarderId,
		impactRadiusId,
		utmCampaign,
		utmContent,
		utmMedium,
		utmSource
	};

	if (['FUND_MARKETING', 'PRESET_PLAN'].includes(leadParams.value.signupFlowType) && props.formConfig.presetPlanId) {
		leadParams.value.presetPlanId = props.formConfig.presetPlanId;
	}

	if (!props.formConfig.referralKey) {
		delete leadParams.value.referralKey;
	}

	if (!leadParams.value.riaFeeWaiverOriginType) {
		delete leadParams.value.riaFeeWaiverOriginType;
	}

	setItemInSessionStorage('lead-capture-params', JSON.stringify(leadParams.value));
}

function setup(): void {
	leadCaptured.value = state.email !== '';
	configureLeadParameters();
}

setup();
</script>

<style lang="scss">
@use '../../styles/constants/colors.scss' as *;

.lead-capture {
	.lead-capture__fieldset {
		border: none;
		margin: 0;
		padding: 0;
		width: 100%;

		.field__input {
			input {
				margin-top: 0 !important;
			}
		}
	}

	.lead-capture__button-primary {
		&.disabled,
		&[disabled] {
			color: #fff !important;
			background-color: token('brand-secondary') !important;
			border-color: token('brand-secondary') !important;
		}
	}

	.lead-capture__error {
		bottom: -25px;
		left: 0;
		position: absolute;
		width: 100%;
		z-index: 1;
	}
}
</style>
